/**
 * features/hr/LeaveCenter.tsx
 * ------------------------------------------------------------------
 * HR DOMAIN — Leave (school-wide).
 *
 * Same operational pattern as the staff-profile Leave tab: pending
 * applications lead, organised under "Awaiting decision". Then
 * recently decided, then older history. Each is a card (not a flat
 * row) with status-coloured left bar. Click a card to open the
 * detail drawer with Approve / Reject.
 *
 * Type filter chips along the top let the HR officer narrow to e.g.
 * just sick leave or just annual leave.
 *
 * "Raise request" stays in the hero — same modal as before, but
 * needs a staff picker because we're not scoped to one staff.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  CalendarDays,
  Plus,
  Check,
  X,
  Loader2,
  Hourglass,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Clock as ClockIcon,
  User as UserIcon,
  FileText as FileTextIcon,
  Ban,
  Pencil,
  Printer,
  Download,
} from 'lucide-react';
import { GradientHero, SoftCard, StatChip, AccentButton, glassFieldClass } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import { useHrData } from '@/features/hr/useHrData';
import { usePublicHolidays } from '@/features/hr/usePublicHolidays';
import { countLeaveDays } from '@/lib/hrLeave';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import { hr, syncUnpaidLeaveDeduction } from '@/features/hr/hrWrite';
import { buildLeaveFormPdf } from '@/lib/leavePdf';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import LeaveBalancesOverview from '@/features/hr/LeaveBalancesOverview';
import LeaveEntitlements from '@/features/hr/LeaveEntitlements';
import LeaveEncashments from '@/features/hr/LeaveEncashments';
import NewLeaveApplication from '@/features/hr/NewLeaveApplication';
import { useLeavePolicies } from '@/features/hr/useLeavePolicies';
import {
  staffFullName,
  staffInitials,
  LEAVE_TYPE_LABELS,
  type HrLeaveRequest,
  type HrStaff,
  type LeaveStatus,
  type LeaveType,
} from '@/lib/hr';
import { leaveBalancesFor, leaveBasis, currentLeaveYear } from '@/lib/leavePolicy';
import { type HrLeaveAllocation } from '@/lib/hrPayroll';
import { salesFor } from '@/lib/leaveEncashment';

const STATUS_TONE: Record<LeaveStatus, { badge: string }> = {
  pending: {
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  approved: {
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  rejected: {
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
  cancelled: {
    badge: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  },
};

type TypeFilter = 'all' | LeaveType;

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const sameMonth =
    s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth();
  if (sameMonth) {
    return `${s.toLocaleDateString([], { day: 'numeric' })}–${e.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  return `${fmtDate(start)} – ${fmtDate(end)}`;
}
function relative(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.round((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) === 1 ? '' : 's'} ago`;
  if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? '' : 's'} ago`;
  return `${Math.floor(days / 365)} year${Math.floor(days / 365) === 1 ? '' : 's'} ago`;
}

interface Props {
  onOpenStaff: (staffId: string) => void;
}

export default function LeaveCenter({ onOpenStaff }: Props) {
  const data = useHrData();
  const { userRoles } = useAuth();
  const perms = getPermissions(userRoles);
  const canApprove = perms.canAccessHR;

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [raising, setRaising] = useState(false);
  const [view, setView] = useState<'applications' | 'balances' | 'entitlements' | 'encashments'>('applications');

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, cancelled: 0 };
    for (const l of data.leave) c[l.status] += 1;
    return c;
  }, [data.leave]);

  const filtered = useMemo(() => {
    return data.leave
      .filter((l) => typeFilter === 'all' || l.type === typeFilter)
      .map((l) => ({ leave: l, staff: data.staffById(l.staffId) }))
      .filter(
        (r): r is { leave: HrLeaveRequest; staff: HrStaff } => !!r.staff,
      );
  }, [data.leave, typeFilter, data.staffById]);

  const pending = useMemo(
    () =>
      filtered
        .filter((r) => r.leave.status === 'pending')
        .sort((a, b) => b.leave.createdAt.localeCompare(a.leave.createdAt)),
    [filtered],
  );

  const recentlyDecided = useMemo(() => {
    const cutoff = Date.now() - 30 * 86400000;
    return filtered
      .filter(
        (r) =>
          (r.leave.status === 'approved' || r.leave.status === 'rejected') &&
          r.leave.decidedAt &&
          new Date(r.leave.decidedAt).getTime() >= cutoff,
      )
      .sort((a, b) =>
        (b.leave.decidedAt ?? '').localeCompare(a.leave.decidedAt ?? ''),
      );
  }, [filtered]);

  const older = useMemo(() => {
    const recentSet = new Set(recentlyDecided.map((r) => r.leave.id));
    return filtered
      .filter(
        (r) =>
          r.leave.status !== 'pending' && !recentSet.has(r.leave.id),
      )
      .sort((a, b) => b.leave.startDate.localeCompare(a.leave.startDate));
  }, [filtered, recentlyDecided]);

  const opened = openId ? data.leave.find((l) => l.id === openId) : null;
  const openedStaff = opened ? data.staffById(opened.staffId) : null;

  return (
    <div className="space-y-6">
      <GradientHero
        icon={CalendarDays}
        title="Leave"
        subtitle="Applications, approvals, balances, sold leave and entitlements"
        actions={
          view === 'applications' ? (
            <AccentButton icon={Plus} onClick={() => setRaising(true)}>
              New application
            </AccentButton>
          ) : undefined
        }
      />

      <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-700">
        {([['applications', 'Applications'], ['balances', 'Balances'], ['encashments', 'Sold leave'], ['entitlements', 'Entitlements']] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setView(id)}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
              view === id
                ? 'border-[var(--accent-strong,#1b3a6b)] text-[var(--accent-link,#2d5295)] dark:text-[var(--accent-link-dark,#93c5fd)]'
                : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'balances' && <LeaveBalancesOverview onOpenStaff={onOpenStaff} canEdit={canApprove} />}
      {view === 'entitlements' && <LeaveEntitlements canEdit={canApprove} />}
      {view === 'encashments' && <LeaveEncashments data={data} canApprove={canApprove} onOpenStaff={onOpenStaff} />}

      {view === 'applications' && (data.loading ? (
        <div className="flex justify-center py-16">
          <Spinner label="Loading leave…" />
        </div>
      ) : (
        <>
          {/* Top-line counts */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip tone="gold" icon={Hourglass} value={counts.pending} label="Pending approval" />
            <StatChip tone="emerald" icon={CheckCircle2} value={counts.approved} label="Approved" />
            <StatChip tone="red" icon={XCircle} value={counts.rejected} label="Rejected" />
            <StatChip tone="slate" value={counts.cancelled} label="Cancelled" />
          </div>

          {/* Type filter */}
          <SoftCard hover={false}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold w-12 shrink-0">
                Type
              </span>
              <div className="flex flex-wrap gap-1.5">
                <FilterChip active={typeFilter === 'all'} onClick={() => setTypeFilter('all')}>
                  All
                </FilterChip>
                {(Object.keys(LEAVE_TYPE_LABELS) as LeaveType[]).map((t) => (
                  <FilterChip
                    key={t}
                    active={typeFilter === t}
                    onClick={() => setTypeFilter(t)}
                  >
                    {LEAVE_TYPE_LABELS[t]}
                  </FilterChip>
                ))}
              </div>
            </div>
          </SoftCard>

          {/* Pending */}
          {pending.length > 0 && (
            <section className="space-y-2">
              <SectionHeader
                tone="amber"
                icon={Hourglass}
                label="Awaiting decision"
                count={pending.length}
              />
              <div className="space-y-2">
                {pending.map(({ leave, staff }) => (
                  <LeaveCard
                    key={leave.id}
                    leave={leave}
                    staff={staff}
                    onCardClick={() => setOpenId(leave.id)}
                    onStaffClick={() => onOpenStaff(staff.id)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Recently decided */}
          {recentlyDecided.length > 0 && (
            <section className="space-y-2">
              <SectionHeader
                tone="slate"
                icon={ClockIcon}
                label="Recently decided"
                count={recentlyDecided.length}
              />
              <div className="space-y-2">
                {recentlyDecided.map(({ leave, staff }) => (
                  <LeaveCard
                    key={leave.id}
                    leave={leave}
                    staff={staff}
                    onCardClick={() => setOpenId(leave.id)}
                    onStaffClick={() => onOpenStaff(staff.id)}
                    muted
                  />
                ))}
              </div>
            </section>
          )}

          {/* Older history */}
          {older.length > 0 && (
            <section className="space-y-2">
              <SectionHeader
                tone="slate"
                label="History"
                count={older.length}
              />
              <div className="space-y-2">
                {older.map(({ leave, staff }) => (
                  <LeaveCard
                    key={leave.id}
                    leave={leave}
                    staff={staff}
                    onCardClick={() => setOpenId(leave.id)}
                    onStaffClick={() => onOpenStaff(staff.id)}
                    muted
                  />
                ))}
              </div>
            </section>
          )}

          {filtered.length === 0 && (
            <SoftCard hover={false}>
              <p className="text-sm text-gray-500 text-center py-6">
                No leave applications match the filter.
              </p>
            </SoftCard>
          )}
        </>
      ))}

      {opened && openedStaff && (
        <LeaveDetailDrawer
          leave={opened}
          staff={openedStaff}
          canApprove={canApprove}
          staffLeave={data.leave.filter((l) => l.staffId === opened.staffId)}
          allocation={data.leaveAllocations.find((a) => a.staffId === opened.staffId)}
          basicSalary={data.payrollProfiles.find((p) => p.staffId === opened.staffId)?.basicSalary ?? 0}
          onClose={() => setOpenId(null)}
          onOpenStaff={onOpenStaff}
        />
      )}

      <NewLeaveApplication open={raising} onClose={() => setRaising(false)} />
    </div>
  );
}

/* ==================================================================
   CARD
   ================================================================== */
function LeaveCard({
  leave,
  staff,
  onCardClick,
  onStaffClick,
  muted,
}: {
  leave: HrLeaveRequest;
  staff: HrStaff;
  onCardClick: () => void;
  onStaffClick: () => void;
  muted?: boolean;
}) {
  const tone = STATUS_TONE[leave.status];
  return (
    <div
      onClick={onCardClick}
      className={`rounded-2xl bg-white dark:bg-gray-800 border ${
        leave.status === 'pending'
          ? 'border-amber-200 dark:border-amber-900/40'
          : 'border-gray-100 dark:border-gray-700'
      } px-4 py-3 hover:shadow-md transition-shadow cursor-pointer ${muted ? 'opacity-95' : ''}`}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onStaffClick();
          }}
          className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-[var(--accent-link-dark,#93c5fd)] flex items-center justify-center text-sm font-bold shrink-0 hover:ring-2 hover:ring-blue-300"
          title="Open staff profile"
        >
          {staffInitials(staff)}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-gray-800 dark:text-gray-100 text-sm truncate">
              {staffFullName(staff)}
            </p>
            <span className="text-xs text-gray-400">·</span>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {LEAVE_TYPE_LABELS[leave.type]} · {leave.days} day{leave.days === 1 ? '' : 's'}
            </p>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {fmtRange(leave.startDate, leave.endDate)}
          </p>
          {leave.reason && (
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1.5 truncate">
              {leave.reason}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <span className={`text-xs font-medium px-2.5 py-1 rounded-lg ${tone.badge}`}>
            {leave.status}
          </span>
          <span className="text-[10px] text-gray-400">
            {leave.status === 'pending'
              ? `raised ${relative(leave.createdAt)}`
              : leave.decidedAt
                ? `decided ${relative(leave.decidedAt)}`
                : ''}
          </span>
        </div>
        <ChevronRight size={16} className="text-gray-300 shrink-0 ml-1" />
      </div>
    </div>
  );
}

/* ==================================================================
   DETAIL DRAWER
   ================================================================== */
function LeaveDetailDrawer({
  leave,
  staff,
  canApprove,
  staffLeave,
  allocation,
  basicSalary,
  onClose,
  onOpenStaff,
}: {
  leave: HrLeaveRequest;
  staff: HrStaff;
  canApprove: boolean;
  staffLeave: HrLeaveRequest[];
  allocation?: HrLeaveAllocation;
  basicSalary: number;
  onClose: () => void;
  onOpenStaff: (id: string) => void;
}) {
  const { userData } = useAuth();
  const { policies } = useLeavePolicies();
  const hrData = useHrData();
  const myDeductions = hrData.deductions.filter((d) => d.staffId === leave.staffId);
  /* Sold days come off the balance this approval is priced against -
     otherwise leave taken out of them is approved as paid. */
  const staffEncashments = salesFor(hrData.leaveEncashments, leave.staffId);
  const [busy, setBusy] = useState<'approve' | 'reject' | 'save' | 'cancel' | null>(null);
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState(leave.startDate.slice(0, 10));
  const [end, setEnd] = useState(leave.endDate.slice(0, 10));
  const tone = STATUS_TONE[leave.status];

  const { isHoliday } = usePublicHolidays();
  const editDays = useMemo(
    () => countLeaveDays(start, end, isHoliday, leave.type),
    [start, end, isHoliday, leave.type],
  );

  const manageable = leave.status === 'pending' || leave.status === 'approved';

  // Balance (approved-only → state BEFORE this pending request) and the
  // unpaid overage that approval would create.
  const balances = leaveBalancesFor({ category: staff.category, allocation, requests: staffLeave, policies, year: currentLeaveYear(), accrualStart: hrData.payrollSettings?.leaveAccrualStart, employmentStart: staff.startDate, encashments: staffEncashments });
  // The same basis for the unpaid-leave check that docks pay (lib/leavePolicy leaveBasis).
  const basis = leaveBasis({ category: staff.category, allocation, policies, year: currentLeaveYear(), accrualStart: hrData.payrollSettings?.leaveAccrualStart, employmentStart: staff.startDate });
  const thisType = balances.find((b) => b.type === leave.type);
  /* Leave never costs pay at this school (21 Oct 2026): going over the
     balance just takes it negative, as on HR's leave cards. So the screen
     shows the balance this leave leaves, and says nothing about pay. */
  const after = thisType && leave.status === 'pending' ? Math.round((thisType.remaining - leave.days) * 100) / 100 : null;

  async function decide(status: 'approved' | 'rejected') {
    setBusy(status === 'approved' ? 'approve' : 'reject');
    try {
      const nowIso = new Date().toISOString();
      await hr.leave.update(leave.id, {
        status,
        approverId: userData?.uid ?? null,
        decidedAt: nowIso,
      });
      onClose();
    } finally {
      setBusy(null);
    }
  }

  async function saveDates() {
    if (editDays <= 0) return;
    setBusy('save');
    try {
      const startISO = new Date(start).toISOString();
      const endISO = new Date(end).toISOString();
      await hr.leave.update(leave.id, { startDate: startISO, endDate: endISO, days: editDays });
      await syncUnpaidLeaveDeduction({
        leave: { ...leave, startDate: startISO, endDate: endISO, days: editDays },
        allocation: basis.allocation, staffLeave, basicSalary, deductions: myDeductions, staffEncashments, calc: basis.opts,
      });
      onClose();
    } finally {
      setBusy(null);
    }
  }

  async function cancelLeave() {
    setBusy('cancel');
    try {
      await hr.leave.update(leave.id, {
        status: 'cancelled',
        approverId: userData?.uid ?? null,
        decidedAt: new Date().toISOString(),
      });
      await syncUnpaidLeaveDeduction({
        leave: { ...leave, status: 'cancelled' },
        allocation: basis.allocation, staffLeave, basicSalary, deductions: myDeductions, staffEncashments, calc: basis.opts,
      });
      onClose();
    } finally {
      setBusy(null);
    }
  }

  async function makeLeavePdf() {
    const logo = await loadLogoDataUrl().catch(() => null);
    return buildLeaveFormPdf({
      staffName: staffFullName(staff),
      jobTitle: staff.jobTitle,
      leave: {
        typeLabel: LEAVE_TYPE_LABELS[leave.type] ?? leave.type,
        range: fmtRange(leave.startDate, leave.endDate),
        days: leave.days,
        reason: leave.reason,
        status: leave.status,
        submitted: leave.createdAt ? fmtDate(leave.createdAt) : undefined,
      },
      balances: balances.map((b) => ({
        label: LEAVE_TYPE_LABELS[b.type as LeaveType] ?? b.type,
        entitlement: b.entitlement,
        taken: b.taken,
        remaining: b.remaining,
      })),
      history: staffLeave
        .filter((l) => l.id !== leave.id)
        .slice()
        .sort((a, b) => b.startDate.localeCompare(a.startDate))
        .map((l) => ({
          typeLabel: LEAVE_TYPE_LABELS[l.type] ?? l.type,
          range: fmtRange(l.startDate, l.endDate),
          days: l.days,
          status: l.status,
        })),
      date: fmtDate(new Date().toISOString()),
    }, logo);
  }
  async function printLeaveForm() {
    try { const url = (await makeLeavePdf()).output('bloburl') as unknown as string; window.open(url, '_blank'); } catch { /* ignore */ }
  }
  async function downloadLeaveForm() {
    try { (await makeLeavePdf()).save(`leave_${staffFullName(staff).replace(/\s+/g, '_')}.pdf`); } catch { /* ignore */ }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-stretch justify-end"
      onClick={onClose}
    >
      <div
        className="bg-white/95 dark:bg-gray-900/85 backdrop-blur-xl border-l border-white/40 dark:border-white/10 w-full max-w-md h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-800 dark:text-gray-100">
              Leave application
            </h2>
            <button
              onClick={() => {
                onClose();
                onOpenStaff(staff.id);
              }}
              className="text-xs text-[var(--accent-link,#2d5295)] hover:underline dark:text-[var(--accent-link-dark,#93c5fd)]"
            >
              {staffFullName(staff)} · {staff.jobTitle}
            </button>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-center justify-between">
            <span className={`text-xs font-medium px-3 py-1.5 rounded-lg ${tone.badge}`}>
              {leave.status}
            </span>
            <span className="text-xs text-gray-400">
              raised {relative(leave.createdAt)}
            </span>
          </div>

          <div className="space-y-1">
            <p className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
              {LEAVE_TYPE_LABELS[leave.type]}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {fmtRange(leave.startDate, leave.endDate)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {leave.days} working day{leave.days === 1 ? '' : 's'}
            </p>
          </div>

          {leave.reason && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-1.5">
                Reason
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                {leave.reason}
              </p>
            </div>
          )}

          <div className="space-y-2.5 border-t border-gray-100 dark:border-gray-700 pt-4">
            <DetailRow icon={UserIcon} label="Applicant" value={staffFullName(staff)} />
            <DetailRow icon={ClockIcon} label="Status" value={leave.status} />
            {leave.decidedAt && (
              <DetailRow
                icon={Check}
                label={leave.status === 'approved' ? 'Approved on' : 'Decided on'}
                value={fmtDate(leave.decidedAt)}
              />
            )}
            <DetailRow icon={FileTextIcon} label="Submitted" value={fmtDate(leave.createdAt)} />
          </div>

          {/* Leave balance — visible while deciding */}
          <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
            <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">
              Leave balance
            </p>
            {leave.type === 'unpaid' ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">Unpaid leave — no balance drawn.</p>
            ) : thisType ? (
              <div className="flex items-center gap-3 text-sm">
                <span className="text-gray-500 dark:text-gray-400">{LEAVE_TYPE_LABELS[leave.type]}:</span>
                <span className="font-semibold text-gray-800 dark:text-gray-100">{thisType.remaining}</span>
                <span className="text-gray-400">of {thisType.entitlement} left</span>
                <span className="text-gray-400">· {thisType.taken} taken · {leave.days} requested</span>
              </div>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-300">No entitlement configured for {LEAVE_TYPE_LABELS[leave.type]}.</p>
            )}

            {after !== null && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Balance after this leave:{' '}
                <span className={`font-semibold tabular-nums ${after < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'}`}>{after}</span>
              </p>
            )}
          </div>

          {leave.status === 'pending' && canApprove && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
              <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2.5">
                Decision
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => decide('approved')}
                  disabled={busy !== null}
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {busy === 'approve' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  Approve
                </button>
                <button
                  onClick={() => decide('rejected')}
                  disabled={busy !== null}
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-60 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                >
                  {busy === 'reject' ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                  Reject
                </button>
              </div>
            </div>
          )}

          {leave.status === 'pending' && !canApprove && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
              <p className="text-xs text-gray-500 italic">
                Awaiting approver decision.
              </p>
            </div>
          )}

          <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
            <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2.5">Approval form</p>
            <div className="flex items-center gap-2">
              <button
                onClick={printLeaveForm}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                <Printer size={14} /> Print form
              </button>
              <button
                onClick={downloadLeaveForm}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                <Download size={14} /> Download PDF
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">A5 form with leave balances and history — for the Head of School to sign before HR records approval here.</p>
          </div>

          {canApprove && manageable && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-4 space-y-3">
              <p className="text-[11px] uppercase tracking-wider text-gray-400">Manage</p>
              {!editing ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditing(true)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    <Pencil size={14} /> Modify dates
                  </button>
                  <button
                    onClick={cancelLeave}
                    disabled={busy !== null}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-60 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                  >
                    {busy === 'cancel' ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                    Cancel leave
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-[11px] text-gray-400 mb-1 block">Start</span>
                      <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={glassFieldClass('py-2 text-sm')} />
                    </label>
                    <label className="block">
                      <span className="text-[11px] text-gray-400 mb-1 block">End</span>
                      <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={glassFieldClass('py-2 text-sm')} />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{editDays} working day{editDays === 1 ? '' : 's'}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={saveDates}
                      disabled={busy !== null || editDays <= 0}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                      {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      Save dates
                    </button>
                    <button
                      onClick={() => { setEditing(false); setStart(leave.startDate.slice(0, 10)); setEnd(leave.endDate.slice(0, 10)); }}
                      className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof UserIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
        <Icon size={13} /> {label}
      </span>
      <span className="text-gray-800 dark:text-gray-100">{value}</span>
    </div>
  );
}

/* ==================================================================
   SECTION HEADER + FILTER CHIP
   ================================================================== */
const TONE_TEXT: Record<'amber' | 'slate', string> = {
  amber: 'text-amber-700 dark:text-amber-300',
  slate: 'text-gray-500 dark:text-gray-400',
};

function SectionHeader({
  tone,
  icon: Icon,
  label,
  count,
}: {
  tone: 'amber' | 'slate';
  icon?: typeof Hourglass;
  label: string;
  count: number;
}) {
  return (
    <div className="flex items-center gap-2 px-1">
      {Icon && <Icon size={14} className={TONE_TEXT[tone]} />}
      <span className={`text-[11px] uppercase tracking-wider font-semibold ${TONE_TEXT[tone]}`}>
        {label}
      </span>
      <span className="text-[11px] text-gray-400">{count}</span>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        active
          ? 'bg-[var(--accent-strong,#1b3a6b)] text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
      }`}
    >
      {children}
    </button>
  );
}

